import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2, Users, UserCheck, CreditCard, Building2, Clock, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { userService } from "@/services/userService";
import { getUserLocationScope } from "@/utils/locationScope";
import {
  getNumericUserStage,
  getNumericUserLevel,
  hasEffectiveNasionalAdminAssignment,
} from "@/utils/privileges";

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalBanks: number;
  activeBanks: number;
  totalBankAccounts: number;
  activeBankAccounts: number;
  totalPaymentRequests: number;
  pendingPaymentRequests: number;
  completedPaymentRequests: number;
}

interface ProvinceGroupedStats {
  provinsi: string;
  cities: Array<{ kabupaten: string; count: number }>;
  totalCount: number;
}

interface MergedCityStat {
  kabupaten: string;
  provinsi: string;
  count: number;
}

// Color palette for different provinces
const PROVINCE_COLORS = [
  "#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088fe",
  "#00c49f", "#ffbb28", "#ff8042", "#8884d8", "#8dd1e1",
  "#d084d0", "#ffb347", "#87ceeb", "#dda0dd", "#f0e68c"
];

interface ProvinceStat {
  provinsi: string;
  count: number;
}

function truncateLabel(label: string, maxLen = 22): string {
  return label.length > maxLen ? label.slice(0, maxLen) + "…" : label;
}

interface BarChartItem {
  name: string;
  label: string;
  count: number;
  color: string;
  tooltip?: string;
  /** Province name when row is a city (for compact legend). */
  group?: string;
}

const CITY_CHART_TOP_N = 12;

const VERTICAL_BAR_TRACK_PX = 200;
/** Space reserved under bars for tilted category names (X axis). */
const X_AXIS_LABEL_AREA_PX = 76;
/** Minimum column width so rotated labels don’t collide. */
const COL_WIDTH_PX = 52;

interface LegendItem {
  id: string;
  label: string;
  color: string;
  count?: number;
}

function ChartLegend({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle?: string;
  items: LegendItem[];
}) {
  if (items.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-border/70 bg-muted/35 px-3 py-3 dark:bg-muted/25">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {subtitle ? (
        <p className="mt-1 text-xs leading-snug text-muted-foreground/90">{subtitle}</p>
      ) : null}
      <ul
        className="mt-2.5 grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        aria-label={title}
      >
        {items.map((it) => (
          <li key={it.id}>
            <div className="flex min-h-[2rem] items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-background/70">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-sm shadow-sm ring-1 ring-border/60"
                style={{ backgroundColor: it.color }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground" title={it.label}>
                {it.label}
              </span>
              {it.count != null && (
                <span className="shrink-0 tabular-nums text-xs font-medium text-muted-foreground">
                  {it.count.toLocaleString("id-ID")}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Vertical column chart — CSS only, scrolls horizontally when there are many categories. */
function LeanDistributionChart({
  data,
  interactive = false,
  onItemClick,
}: {
  data: BarChartItem[];
  interactive?: boolean;
  onItemClick?: (item: BarChartItem) => void;
}) {
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const gapPx = 8;
  const scrollMinWidth = Math.max(
    data.length * COL_WIDTH_PX + Math.max(0, data.length - 1) * gapPx,
    120
  );

  const colClass = "flex w-[var(--col-w)] shrink-0 flex-col items-stretch";
  const colStyle = { ["--col-w" as string]: `${COL_WIDTH_PX}px` } as React.CSSProperties;

  const axisLabel = (item: BarChartItem, title: string) => (
    <div
      className="relative w-full shrink-0 overflow-visible"
      style={{ height: X_AXIS_LABEL_AREA_PX }}
    >
      <span
        className="absolute bottom-0 left-1/2 z-0 origin-bottom whitespace-nowrap text-left text-[11px] font-medium leading-tight text-muted-foreground sm:text-xs"
        style={{
          transform: "translateX(-50%) rotate(-42deg)",
        }}
        title={title}
      >
        {item.name}
      </span>
    </div>
  );

  return (
    <div className="w-full overflow-x-auto pb-2" aria-label="Distribusi anggota">
      <ul
        className="flex flex-row items-end gap-2 pt-1"
        style={{ minWidth: scrollMinWidth }}
      >
        {data.map((item) => {
          const pct = Math.max((item.count / maxCount) * 100, item.count > 0 ? 3 : 0);
          const title = `${item.tooltip ?? item.name}: ${item.count.toLocaleString("id-ID")} anggota`;
          const rowKey = item.group ? `${item.group}:${item.name}` : item.name;

          const column = (
            <>
              <span className="mb-0.5 block min-h-[2.5rem] text-center text-[10px] sm:text-xs tabular-nums font-semibold text-foreground leading-tight">
                {item.count.toLocaleString("id-ID")}
              </span>
              <div
                className="w-full shrink-0 overflow-hidden rounded-t-md bg-muted/60"
                style={{ height: VERTICAL_BAR_TRACK_PX }}
              >
                <div className="flex h-full w-full flex-col justify-end">
                  <div
                    className="w-full rounded-t-md"
                    style={{
                      height: `${pct}%`,
                      minHeight: item.count > 0 ? 4 : 0,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
              {axisLabel(item, title)}
            </>
          );

          if (interactive && onItemClick) {
            return (
              <li key={rowKey} className={colClass} style={colStyle}>
                <button
                  type="button"
                  onClick={() => onItemClick(item)}
                  className="flex w-full flex-col items-stretch rounded-md px-0.5 py-0.5 transition-colors hover:bg-muted/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {column}
                </button>
              </li>
            );
          }

          return (
            <li key={rowKey} className={colClass} style={colStyle}>
              {column}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const isOrdinaryUser = false;

  const numericLevel = getNumericUserLevel(user);
  const isSuperAdminDashboard = numericLevel === 3;
  const userStage = getNumericUserStage(user);

  const hasNasionalLevel = React.useMemo(
    () => hasEffectiveNasionalAdminAssignment(user),
    [user]
  );

  /** Nasional assignment: provinsi-only chart — bukan untuk admin wilayah (stage kota/provinsi). */
  const showNasionalProvinceChart =
    hasNasionalLevel && userStage !== 2 && userStage !== 1;

  const [drillProvince, setDrillProvince] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDrillProvince(null);
  }, [user?.id]);

  const [provinceGroupedStats, setProvinceGroupedStats] = React.useState<ProvinceGroupedStats[]>([]);
  const [mergedCityStats, setMergedCityStats] = React.useState<MergedCityStat[]>([]);
  const [provinceStats, setProvinceStats] = React.useState<ProvinceStat[]>([]);
  const [provinceColorMap, setProvinceColorMap] = React.useState<Map<string, string>>(new Map());
  const [loadingStats, setLoadingStats] = React.useState(true);
  const [dashboardStats, setDashboardStats] = React.useState<DashboardStats | null>(null);
  const [loadingDashboardStats, setLoadingDashboardStats] = React.useState(true);

  React.useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoadingDashboardStats(true);
        const result = await userService.getDashboardStats();
        setDashboardStats(result.stats);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoadingDashboardStats(false);
      }
    };

    fetchDashboardStats();
  }, []);

  React.useEffect(() => {
    const fetchCityStats = async () => {
      if (!user) return;

      try {
        setLoadingStats(true);

        await getUserLocationScope(user);

        const result = await userService.getUserStatsByProvince();

        if (numericLevel === 3) {
          const provinceMap = new Map<string, number>();
          const grouped = new Map<string, Array<{ kabupaten: string; count: number }>>();

          result.stats.forEach((stat) => {
            if (!stat.provinsi || !stat.provinsi.trim()) return;
            const provinsiKey = stat.provinsi.trim();
            provinceMap.set(provinsiKey, (provinceMap.get(provinsiKey) || 0) + stat.count);
            if (!grouped.has(provinsiKey)) {
              grouped.set(provinsiKey, []);
            }
            grouped.get(provinsiKey)!.push({
              kabupaten: stat.kabupaten,
              count: stat.count,
            });
          });

          const provinceData: ProvinceStat[] = Array.from(provinceMap.entries())
            .map(([provinsi, count]) => ({ provinsi, count }))
            .sort((a, b) => b.count - a.count);

          setProvinceStats(provinceData);

          const provinceGrouped: ProvinceGroupedStats[] = Array.from(grouped.entries()).map(([provinsi, cities]) => ({
            provinsi,
            cities: cities.sort((a, b) => b.count - a.count),
            totalCount: cities.reduce((sum, city) => sum + city.count, 0),
          }));
          provinceGrouped.sort((a, b) => b.totalCount - a.totalCount);
          setProvinceGroupedStats(provinceGrouped);

          const merged: MergedCityStat[] = [];
          const colorMap = new Map<string, string>();
          provinceGrouped.forEach((province, index) => {
            const provinsi = province.provinsi;
            if (!provinsi) return;
            const provinsiName = provinsi.trim();
            if (!provinsiName) return;
            colorMap.set(String(provinsiName), PROVINCE_COLORS[index % PROVINCE_COLORS.length]!);
            province.cities.forEach((city) => {
              merged.push({
                kabupaten: city.kabupaten,
                provinsi: provinsiName,
                count: city.count,
              });
            });
          });
          setMergedCityStats(merged);
          setProvinceColorMap(colorMap);
        } else if (showNasionalProvinceChart) {
          setProvinceGroupedStats([]);
          setMergedCityStats([]);
          const provinceMap = new Map<string, number>();

          result.stats.forEach((stat) => {
            if (!stat.provinsi || !stat.provinsi.trim()) return;
            const provinsiKey = stat.provinsi.trim();
            const currentCount = provinceMap.get(provinsiKey) || 0;
            provinceMap.set(provinsiKey, currentCount + stat.count);
          });

          const provinceData: ProvinceStat[] = Array.from(provinceMap.entries())
            .map(([provinsi, count]) => ({ provinsi, count }))
            .sort((a, b) => b.count - a.count);

          setProvinceStats(provinceData);

          const colorMap = new Map<string, string>();
          provinceData.forEach((province, index) => {
            if (!province || !province.provinsi) return;
            colorMap.set(String(province.provinsi), PROVINCE_COLORS[index % PROVINCE_COLORS.length]!);
          });

          setProvinceColorMap(colorMap);
        } else {
          const grouped = new Map<string, Array<{ kabupaten: string; count: number }>>();

          result.stats.forEach((stat) => {
            if (!grouped.has(stat.provinsi)) {
              grouped.set(stat.provinsi, []);
            }
            grouped.get(stat.provinsi)!.push({
              kabupaten: stat.kabupaten,
              count: stat.count,
            });
          });

          const provinceGrouped: ProvinceGroupedStats[] = Array.from(grouped.entries()).map(([provinsi, cities]) => ({
            provinsi,
            cities: cities.sort((a, b) => b.count - a.count),
            totalCount: cities.reduce((sum, city) => sum + city.count, 0),
          }));

          provinceGrouped.sort((a, b) => b.totalCount - a.totalCount);

          setProvinceGroupedStats(provinceGrouped);

          const merged: MergedCityStat[] = [];
          const colorMap = new Map<string, string>();

          provinceGrouped.forEach((province, index) => {
            const provinsi = province.provinsi;
            if (!provinsi) return;
            const provinsiName = provinsi.trim();
            if (!provinsiName) return;

            colorMap.set(String(provinsiName), PROVINCE_COLORS[index % PROVINCE_COLORS.length]!);

            province.cities.forEach((city) => {
              merged.push({
                kabupaten: city.kabupaten,
                provinsi: provinsiName,
                count: city.count,
              });
            });
          });

          setProvinceStats([]);
          setMergedCityStats(merged);
          setProvinceColorMap(colorMap);
        }
      } catch (error) {
        console.error("Error fetching city stats:", error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchCityStats();
  }, [user, showNasionalProvinceChart]);

  const statsCards = [
    {
      title: "Total Anggota",
      value: dashboardStats?.totalUsers ?? 0,
      description: "Jumlah total anggota terdaftar",
      icon: Users,
    },
    {
      title: "Anggota Aktif",
      value: dashboardStats?.activeUsers ?? 0,
      description: "Anggota dengan status aktif",
      icon: UserCheck,
    },
    {
      title: "Transaksi",
      value: dashboardStats?.totalPaymentRequests ?? 0,
      description: `${dashboardStats?.completedPaymentRequests ?? 0} selesai`,
      icon: CreditCard,
    },
    {
      title: "Pending",
      value: dashboardStats?.pendingPaymentRequests ?? 0,
      description: "Menunggu pembayaran",
      icon: Clock,
    },
    {
      title: "Bank Aktif",
      value: dashboardStats?.activeBanks ?? 0,
      description: `dari ${dashboardStats?.totalBanks ?? 0} bank`,
      icon: Building2,
    },
    {
      title: "Rekening Aktif",
      value: dashboardStats?.activeBankAccounts ?? 0,
      description: `dari ${dashboardStats?.totalBankAccounts ?? 0} rekening`,
      icon: CheckCircle2,
    },
  ];

  const provinceChartData: BarChartItem[] = provinceStats.map((p, index) => ({
    name: p.provinsi,
    label: truncateLabel(p.provinsi),
    count: p.count,
    color: provinceColorMap.get(p.provinsi) ?? PROVINCE_COLORS[index % PROVINCE_COLORS.length]!,
  }));

  const cityChartData: BarChartItem[] = mergedCityStats.slice(0, CITY_CHART_TOP_N).map((c, index) => ({
    name: c.kabupaten,
    label: truncateLabel(c.kabupaten, 22),
    count: c.count,
    color: provinceColorMap.get(c.provinsi) ?? PROVINCE_COLORS[index % PROVINCE_COLORS.length]!,
    tooltip: `${c.kabupaten} (${c.provinsi})`,
    group: c.provinsi,
  }));

  /** Provinces appearing in the top‑N city chart, with summed counts (in that slice). */
  const cityLegendItems = React.useMemo((): LegendItem[] => {
    const totals = new Map<string, number>();
    for (const c of mergedCityStats.slice(0, CITY_CHART_TOP_N)) {
      const p = c.provinsi?.trim();
      if (!p) continue;
      totals.set(p, (totals.get(p) || 0) + c.count);
    }
    return Array.from(totals.entries())
      .map(([label, count]) => ({
        id: label,
        label,
        color: provinceColorMap.get(label) ?? "#94a3b8",
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [mergedCityStats, provinceColorMap]);

  /** Province‑level chart: same colors as bars, sorted by count. */
  const provinceDistributionLegendItems = React.useMemo((): LegendItem[] => {
    const rows = provinceStats.map((p, index) => ({
      id: p.provinsi,
      label: p.provinsi,
      count: p.count,
      color: provinceColorMap.get(p.provinsi) ?? PROVINCE_COLORS[index % PROVINCE_COLORS.length]!,
    }));
    return rows.sort((a, b) => b.count - a.count);
  }, [provinceStats, provinceColorMap]);

  const drilledCityChartData: BarChartItem[] = React.useMemo(() => {
    if (!isSuperAdminDashboard || !drillProvince) return [];
    const pg = provinceGroupedStats.find((p) => p.provinsi === drillProvince);
    if (!pg) return [];
    const color = provinceColorMap.get(drillProvince) ?? "#8884d8";
    return pg.cities.map((c) => ({
      name: c.kabupaten,
      label: truncateLabel(c.kabupaten, 22),
      count: c.count,
      color,
      tooltip: `${c.kabupaten} (${drillProvince})`,
      group: drillProvince,
    }));
  }, [isSuperAdminDashboard, drillProvince, provinceGroupedStats, provinceColorMap]);

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
      </div>

      {isOrdinaryUser && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <CardHeader>
            <CardTitle className="text-blue-900 dark:text-blue-100">Ordinary User</CardTitle>
            <CardDescription className="text-blue-700 dark:text-blue-300">
              You are logged in as an ordinary user. Your access level is limited.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Stats Grid */}
      {loadingDashboardStats ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-muted rounded" />
                <div className="h-4 w-4 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-20 bg-muted rounded mb-2" />
                <div className="h-3 w-32 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="relative overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                    <Icon className="h-4 w-4 text-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tabular-nums">{stat.value.toLocaleString("id-ID")}</div>
                  <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isSuperAdminDashboard
              ? drillProvince
                ? `Kota/Kabupaten — ${drillProvince}`
                : "Distribusi Anggota per Provinsi"
              : showNasionalProvinceChart
                ? "Distribusi Anggota per Provinsi"
                : "Distribusi Anggota per Kota/Kabupaten"}
          </CardTitle>
          <CardDescription>
            {isSuperAdminDashboard
              ? drillProvince
                ? "Jumlah anggota per kota/kabupaten di provinsi ini."
                : "Klik sebuah provinsi untuk melihat detail kota/kabupaten."
              : showNasionalProvinceChart
                ? "Jumlah anggota berdasarkan provinsi"
                : numericLevel === 2
                  ? "Kota/kabupaten di wilayah provinsi Anda (admin)."
                  : numericLevel === 1
                    ? "Anggota di kota/kabupaten wilayah Anda (admin)."
                    : `Top ${cityChartData.length} kota/kabupaten berdasarkan jumlah anggota`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingStats ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full h-32 bg-muted rounded-t-md animate-pulse" style={{ animationDelay: `${i * 50}ms` }} />
                    <div className="w-full h-4 bg-muted rounded animate-pulse" style={{ animationDelay: `${i * 50}ms` }} />
                  </div>
                ))}
              </div>
            </div>
          ) : isSuperAdminDashboard ? (
            drillProvince ? (
              drilledCityChartData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
                  Tidak ada data kota/kabupaten.
                </div>
              ) : (
                <div className="space-y-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => setDrillProvince(null)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Kembali ke provinsi
                  </Button>
                  <LeanDistributionChart data={drilledCityChartData} />
                </div>
              )
            ) : provinceChartData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
                Tidak ada data tersedia.
              </div>
            ) : (
              <>
                <ChartLegend
                  title="Keterangan warna — provinsi"
                  items={provinceDistributionLegendItems}
                />
                <LeanDistributionChart
                  data={provinceChartData}
                  interactive
                  onItemClick={(item) => setDrillProvince(item.name)}
                />
              </>
            )
          ) : showNasionalProvinceChart ? (
            provinceChartData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
                Tidak ada data tersedia.
              </div>
            ) : (
              <>
                <ChartLegend
                  title="Keterangan warna — provinsi"
                  items={provinceDistributionLegendItems}
                />
                <LeanDistributionChart data={provinceChartData} />
              </>
            )
          ) : cityChartData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
              Tidak ada data tersedia.
            </div>
          ) : (
            <>
              <ChartLegend
                title="Warna batang = provinsi kota"
                subtitle={`Jumlah di kolom kanan: total anggota kota yang tampil (top ${CITY_CHART_TOP_N}) per provinsi.`}
                items={cityLegendItems}
              />
              <LeanDistributionChart data={cityChartData} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
